/**
 * Execute promises with limited concurrency
 *
 * @param tasks - Array of functions that return promises
 * @param limit - Maximum number of concurrent promises
 * @returns Promise that resolves to array of results
 *
 * @example
 * const tasks = items.map(item => () => fetchData(item))
 * const results = await promiseAllWithLimit(tasks, 5) // Max 5 concurrent requests
 */
export async function promiseAllWithLimit<T>(tasks: (() => Promise<T>)[], limit: number): Promise<T[]> {
  // Validate limit parameter
  if (!Number.isInteger(limit) || limit <= 0) {
    throw new RangeError('limit must be a positive integer')
  }

  const results: T[] = []
  const executing: Promise<unknown>[] = []

  for (let i = 0; i < tasks.length; i += 1) {
    const task = tasks[i]

    // Create promise that executes task, stores result, and removes itself when done
    const promise = task()
      .then(result => {
        results[i] = result
      })
      .then(() => {
        // Remove this promise from executing pool when it finishes
        const index = executing.indexOf(promise)
        if (index !== -1) {
          executing.splice(index, 1)
        }
      })

    // Add to executing pool
    executing.push(promise)

    // When limit is reached, wait for at least one to finish
    if (executing.length >= limit) {
      // eslint-disable-next-line no-await-in-loop -- Intentional: this is how we limit concurrency
      await Promise.race(executing)
    }
  }

  // Wait for remaining promises
  await Promise.all(executing)

  return results
}
